import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/Bits";
import { DemoBadge } from "@/components/DemoBadge";
import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip, ReferenceLine } from "recharts";
import { toast } from "sonner";

export default function AppBacktesting() {
  const [assets, setAssets] = useState([]);
  const [cfg, setCfg] = useState({ symbol: "BTC/USD", timeframe: "4h", risk_per_trade: 1.0, period_bars: 180 });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/markets").then((r) => setAssets(r.data.assets)).catch(() => {}); }, []);

  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/backtest", cfg); setRes(data); }
    catch { toast.error("Backtest failed"); }
    finally { setBusy(false); }
  };

  return (
    <DashboardLayout title="Backtesting">
      <div className="mb-4 flex items-center gap-2"><DemoBadge /><span className="text-sm text-muted-foreground">EMA-cross strategy on demo data. Past performance does not guarantee future results.</span></div>

      <div className="grid gap-6 lg:grid-cols-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-display text-lg font-semibold">Configuration</h3>
          <div className="mt-4 space-y-3 text-sm">
            <div>
              <label className="mb-1 block text-[10px] uppercase text-muted-foreground">Asset</label>
              <select value={cfg.symbol} onChange={(e) => setCfg({ ...cfg, symbol: e.target.value })} data-testid="bt-symbol" className="w-full rounded-lg border border-border bg-card px-3 py-2">
                {assets.map((a) => <option key={a.symbol} value={a.symbol}>{a.symbol}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase text-muted-foreground">Timeframe</label>
              <select value={cfg.timeframe} onChange={(e) => setCfg({ ...cfg, timeframe: e.target.value })} data-testid="bt-tf" className="w-full rounded-lg border border-border bg-card px-3 py-2">
                {["1h", "4h", "1D"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase text-muted-foreground">Risk per trade</label>
              <div className="grid grid-cols-4 gap-1">
                {[0.25, 0.5, 1, 2].map((r) => <button key={r} onClick={() => setCfg({ ...cfg, risk_per_trade: r })} data-testid={`bt-risk-${r}`} className={`rounded-lg py-1.5 text-xs ${cfg.risk_per_trade === r ? "bg-primary text-primary-foreground" : "border border-border"}`}>{r}%</button>)}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase text-muted-foreground">Period: {cfg.period_bars} bars</label>
              <input type="range" min="60" max="300" step="20" value={cfg.period_bars} onChange={(e) => setCfg({ ...cfg, period_bars: +e.target.value })} className="w-full" />
            </div>
            <button onClick={run} disabled={busy} data-testid="bt-run" className="w-full rounded-xl bg-primary py-2.5 font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Running…" : "Run Backtest"}</button>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-6">
          {!res ? (
            <div className="flex h-64 items-center justify-center rounded-2xl border border-dashed border-border text-muted-foreground" data-testid="bt-empty">Configure a strategy and run a backtest to see results.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatCard label="Net Return" value={`${res.net_return_pct}%`} accent={res.net_return_pct >= 0 ? "text-bullish" : "text-bearish"} testid="bt-net" />
                <StatCard label="Win Rate" value={`${res.win_rate}%`} />
                <StatCard label="Max Drawdown" value={`${res.max_drawdown}%`} accent="text-warning" />
                <StatCard label="Profit Factor" value={res.profit_factor} />
                <StatCard label="Total Trades" value={res.total_trades} />
                <StatCard label="Wins / Losses" value={`${res.wins}/${res.losses}`} />
                <StatCard label="Best Trade" value={`$${fmt(res.best_trade)}`} accent="text-bullish" />
                <StatCard label="Worst Trade" value={`$${fmt(res.worst_trade)}`} accent="text-bearish" />
              </div>
              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="mb-3 font-display text-lg font-semibold">Equity Curve</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={res.equity_curve}>
                    <YAxis domain={["auto", "auto"]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={55} />
                    <XAxis dataKey="i" hide />
                    <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v) => [`$${fmt(v)}`, "Equity"]} />
                    <ReferenceLine y={10000} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" />
                    <Line type="monotone" dataKey="equity" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
